// src/components/learning/PostLesson.tsx
import React, { useState } from "react";
import styled from "styled-components";

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 30px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  font-family: "Roboto", sans-serif;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.primary || "#7e22ce"};
  font-size: 1.8rem;
  margin-bottom: 15px;
`;

const SectionContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #333;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 ratio */
  margin-bottom: 20px;
`;

const VideoFrame = styled.iframe`
  position: absolute;
  width: 100%;
  height: 100%;
  border: none;
`;

const List = styled.ul`
  list-style: disc;
  margin-left: 20px;
`;

const ListItem = styled.li`
  margin: 8px 0;
`;

const InputArea = styled.textarea`
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ccc;
  margin-top: 10px;
  border-radius: 4px;
  resize: vertical;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px;
  margin-top: 10px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Button = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  background-color: ${({ theme }) => theme.primary || "#7e22ce"};
  color: #fff;
  font-size: 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: ${({ theme }) => theme.primaryDark || "#5a17a7"};
  }
`;

const PostLesson: React.FC<{ onFinish?: () => void }> = ({ onFinish }) => {
  // State for final quiz question (example)
  const [quizAnswer, setQuizAnswer] = useState("");
  const [quizFeedback, setQuizFeedback] = useState("");
  // State for practical challenge input
  const [challengeResponse, setChallengeResponse] = useState("");
  // State for feedback survey
  const [feedback, setFeedback] = useState("");

  const handleQuizSubmit = () => {
    // Example quiz: Which chamber pumps oxygenated blood?
    if (quizAnswer === "Left Ventricle") {
      setQuizFeedback("Correct! The left ventricle pumps oxygenated blood to the body.");
    } else {
      setQuizFeedback("Incorrect. The correct answer is Left Ventricle.");
    }
  };

  const handleFinish = () => {
    if (onFinish) {
      onFinish();
    } else {
      alert("Thank you for completing the lesson!");
    }
  };

  return (
    <Container>
      {/* Summarization and Consolidation */}
      <Section>
        <SectionTitle>Lesson Summary</SectionTitle>
        <VideoContainer>
          <VideoFrame
            title="Lesson Summary"
            src="https://www.youtube.com/embed/dummy-summary-video" // Replace with your actual summary video URL
            allowFullScreen
          />
        </VideoContainer>
        <SectionContent>
          <List>
            <ListItem>The heart has four main chambers: two atria and two ventricles.</ListItem>
            <ListItem>The left ventricle is responsible for pumping oxygenated blood.</ListItem>
            <ListItem>Regular exercise and a balanced diet are key for maintaining heart health.</ListItem>
          </List>
        </SectionContent>
      </Section>

      {/* Assessment and Reinforcement */}
      <Section>
        <SectionTitle>Final Quiz</SectionTitle>
        <SectionContent>
          Which chamber of the heart is primarily responsible for pumping oxygenated blood to the body?
        </SectionContent>
        <div>
          <label>
            <input
              type="radio"
              name="quiz"
              value="Left Atrium"
              onChange={(e) => setQuizAnswer(e.target.value)}
            />{" "}
            Left Atrium
          </label>
          <br />
          <label>
            <input
              type="radio"
              name="quiz"
              value="Right Atrium"
              onChange={(e) => setQuizAnswer(e.target.value)}
            />{" "}
            Right Atrium
          </label>
          <br />
          <label>
            <input
              type="radio"
              name="quiz"
              value="Left Ventricle"
              onChange={(e) => setQuizAnswer(e.target.value)}
            />{" "}
            Left Ventricle
          </label>
          <br />
          <label>
            <input
              type="radio"
              name="quiz"
              value="Right Ventricle"
              onChange={(e) => setQuizAnswer(e.target.value)}
            />{" "}
            Right Ventricle
          </label>
        </div>
        <Button onClick={handleQuizSubmit}>Submit Quiz Answer</Button>
        {quizFeedback && <SectionContent>{quizFeedback}</SectionContent>}
      </Section>

      <Section>
        <SectionTitle>Practical Challenge</SectionTitle>
        <SectionContent>
          Describe or design a simple poster that illustrates the heart's blood flow.
        </SectionContent>
        <InputArea
          rows={4}
          placeholder="Type your design description here..."
          value={challengeResponse}
          onChange={(e) => setChallengeResponse(e.target.value)}
        />
      </Section>

      {/* Post-Lesson Engagement */}
      <Section>
        <SectionTitle>Feedback Survey</SectionTitle>
        <SectionContent>
          How clear and interactive was this lesson? Please provide your feedback:
        </SectionContent>
        <TextInput
          type="text"
          placeholder="Enter your feedback here..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
      </Section>

      <Section>
        <SectionTitle>Next Steps & Further Reading</SectionTitle>
        <SectionContent>
          <List>
            <ListItem>
              <a href="https://www.example.com/additional-video" target="_blank" rel="noreferrer">
                Additional Heart Insights Video
              </a>
            </ListItem>
            <ListItem>
              <a href="https://www.example.com/article" target="_blank" rel="noreferrer">
                The Science of Heart Health
              </a>
            </ListItem>
            <ListItem>
              Join our community forum for discussions and Q&A.
            </ListItem>
          </List>
        </SectionContent>
      </Section>

      <Section>
        <SectionTitle>Progress & Rewards</SectionTitle>
        <SectionContent>
          Congratulations! You have completed the lesson and earned the "Heart Health Advocate" badge.
        </SectionContent>
      </Section>

      <Button onClick={handleFinish}>Finish Lesson</Button>
    </Container>
  );
};

export default PostLesson;
